










































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';

import OfferFreeCoinsService from '@/api/OfferFreeCoinsService';

import { CustomerSettings } from '@/components/organisms/OfferFreeCoins/FormSteps';
import { Surface, Typography, Picture } from '@/components/atoms';
import { FilledButton } from '@/components/molecules';
import CustomersIcon from '@/components/icons/Customers.vue';

import {
  Campaign,
  TCustomerLimitType
} from '@/models/marketingCenter/freeCoins';

import eventBus from '@/utils/eventBus';
import { treatError } from '@/utils';
import segmentIo from '@/utils/segment.io';

type FormData = {
  customerLimitType: TCustomerLimitType;
  customerLimit?: string;
  customerDailyLimit?: string;
  onlyNewCustomers: boolean;
};

@Component({
  components: {
    Surface,
    Typography,
    Picture,
    FilledButton,
    CustomersIcon,
    CustomerSettings
  }
})
export default class CampaignCustomerLimitEditModal extends Vue {
  @Prop() readonly campaign!: Campaign;
  @Prop({ default: false }) isDetail?: boolean;

  offerFreeCoinsService: OfferFreeCoinsService = new OfferFreeCoinsService();

  buttonDisabled = true;
  errors: string[] = [];
  form: FormData = {
    customerLimitType: this.campaign.limitType as TCustomerLimitType,
    onlyNewCustomers: !this.campaign.allowRepeatingCustomers,
    customerLimit:
      this.campaign.limitType === 'total-customer'
        ? String(this.campaign.customerLimit)
        : undefined,
    customerDailyLimit:
      this.campaign.limitType === 'daily-customer'
        ? String(this.campaign.customerLimit)
        : undefined
  };

  mounted() {
    segmentIo.modal('Free Coins/Edit customer limit');
  }

  @Watch('form', { deep: true })
  enableSubmitButton() {
    this.buttonDisabled = false;
  }

  setNewError(err: string) {
    this.errors = [...this.errors, err];
  }

  validate() {
    const newErrors: string[] = [];

    const type = this.form.customerLimitType;

    const validationMapper: Record<string, any> = {
      'total-customer': () => {
        if (!this.form.customerLimit) {
          newErrors.push('customerLimit');
        }
      },
      'daily-customer': () => {
        if (!this.form.customerDailyLimit) {
          newErrors.push('customerDailyLimit');
        }
      }
    };

    this.errors = newErrors;

    if (validationMapper[type]) {
      validationMapper[type]();
    }

    return !newErrors.length;
  }

  onClose() {
    eventBus.$emit('closeModal');
  }

  toggleNewCustomers() {
    this.form.onlyNewCustomers = !this.form.onlyNewCustomers;
  }

  changeCustomerLimit(value: string) {
    switch (this.form.customerLimitType) {
      case 'total-customer':
        this.form.customerLimit = value;
        break;

      case 'daily-customer':
        this.form.customerDailyLimit = value;
        break;

      default:
        break;
    }
  }

  get coins() {
    return Number(this.campaign.coinsReward);
  }

  async onConfirm() {
    if (!this.campaign) return;

    const validation = this.validate();

    if (!validation) {
      this.$notification('Please fill the highlighted fields below', 'error');
      return;
    }

    // If the AllowRepeatingCustomers field is true, customers who have already redeemed coins will continue to be able to redeem new coins
    const payload = {
      ...this.campaign,
      allowRepeatingCustomers: this.form.onlyNewCustomers ? false : true,
      limitType: this.form.customerLimitType
    } as Campaign;

    if (!payload.dateLimit) payload.dateLimit = undefined;

    switch (this.form.customerLimitType) {
      case 'total-customer':
        payload.customerLimit = Number(this.form.customerLimit);
        break;

      case 'daily-customer':
        payload.customerLimit = Number(this.form.customerDailyLimit);
        break;

      default:
        payload.customerLimit = undefined;
        break;
    }

    try {
      this.$showLoaderWithLocker();

      const response = await this.offerFreeCoinsService.updateOfferFreeCoin(
        this.campaign.id as string,
        payload
      );

      if (response.status !== 200) return;

      eventBus.$emit('updateFreeCoinDetailCampaign', payload);
      eventBus.$emit('updateFreeCoinCampaignList', payload);

      this.$notification('Campaign member limit successfully updated');
      this.onClose();

      this.$analytics({
        eventCategory: `Free Coins Campaign ${
          this.isDetail ? 'Detail' : ''
        } | Edit Customer Settings`,
        eventAction: 'click',
        eventLabel: `Edited the campaign ${
          this.isDetail ? 'detail' : ''
        } customer limit`
      });
    } catch (e) {
      treatError(e);
    } finally {
      this.$hideLoader();
    }
  }
}
